import {
  change_password,
  edit_profile,
} from "@/constants/translation/auth.translation";
import { validation } from "@/constants/validation.translation";
import { validation as commonValidation } from "@/constants/translation/common.translation";
import { 
  OTP_REGEX, 
  PHONE_NUMBER_REGEX, 
  EMAIL_REGEX, 
  NAME_REGEX, 
  FULL_NAME_REGEX, 
  PASSWORD_REGEX, 
  OTP_WITH_SPACES_REGEX,
  VALIDATION_CONSTANTS 
} from "@/helpers/common";

import * as yup from "yup";

/**
 * @name validationHelper
 * @description A class containing static methods for validating various input fields such as email, password, OTP, etc., using the `yup` validation library.
 * It includes validation methods for sign-in, forgot password, OTP verification, and password reset.
 */
class validationHelper {

  /**
   * @name signInValidation
   * @description Validates the email and password input fields for sign-in.
   * @returns {Object} A Yup validation schema for sign-in form.
   * @throws {Error} If validation fails (missing or invalid fields).
   */
  static signInValidation() {
    const { login } = validation;
    return yup.object({
      email: yup
        .string()
        .required(login.email.required)
        .matches(EMAIL_REGEX, login.email.pattern),
      password: yup.string().required(login.password.required),
    });
  }

  /**
   * @name forgotPasswordValidation
   * @description Validates the email input field for forgot password functionality.
   * @returns {Object} A Yup validation schema for forgot password form.
   * @throws {Error} If validation fails (missing or invalid email).
   */
  static forgotPasswordValidation() {
    const { forgot_password } = validation;
    return yup.object({
      email: yup
        .string()
        .required(forgot_password.email.required)
        .matches(EMAIL_REGEX, forgot_password.email.pattern),
    });
  }

  /**
   * @name verifyOtpValidation
   * @description Validates the OTP input field for OTP verification.
   * @returns {Object} A Yup validation schema for OTP verification form.
   * @throws {Error} If validation fails (missing or invalid OTP).
   */
  static verifyOtpValidation() {
    const { otp_verification } = validation;
    return yup.object({
      otp: yup
        .string()
        .required(otp_verification.otp.required)
        .matches(OTP_REGEX, otp_verification.otp.pattern),
    });
  }

  /**
   * @name otpVerifactionValidation
   * @description Validates the OTP input field for OTP verification.
   * @returns {Object} A Yup validation schema for OTP verification form.
   * @throws {Error} If validation fails (missing or invalid OTP).
   */
  static otpVerifactionValidation() {
    const { otp_verification } = validation;
    return yup.object({
      otp: yup
        .string()
        .required(otp_verification.otp.required)
        .matches(OTP_WITH_SPACES_REGEX, otp_verification.otp.pattern),
    });
  }

  /**
   * @name resetPasswordValidation
   * @description Validates the new password and confirm password input fields for resetting the password.
   * @returns {Object} A Yup validation schema for reset password form.
   * @throws {Error} If validation fails (password requirements or mismatch).
   */
  static resetPasswordValidation() {
    const { reset_password } = validation;
    return yup.object({
      new_password: yup
        .string()
        .required(reset_password.new_password.required)
        .matches(
          PASSWORD_REGEX,
          reset_password.new_password.pattern
        ),
      confirm_password: yup
        .string()
        .required(reset_password.confirm_password.same_password)
        .oneOf(
          [yup.ref("new_password"), null],
          reset_password.confirm_password.same_password
        ),
    });
  }

  /**
   * @name changePasswordValidation
   * @description Validates the current password, new password, and confirm password input fields for changing the admin password.
   * @param {function} t - Translation function
   * @returns {Object} A Yup validation schema for the change password form.
   * @throws {Error} If validation fails (missing fields, invalid password format, or password mismatch).
   * 
   * API Requirements:
   * - current_password: Required
   * - new_password: Min 8 chars, Max 255 chars, must contain uppercase, lowercase, number, special character
   */
  static changePasswordValidation(t) {
    return yup.object({
      current_password: yup
        .string()
        .required(t(change_password.validation.old_password?.required || "Current password is required")),
      new_password: yup
        .string()
        .test('password-validation', t(change_password.validation.new_password?.pattern || "Password must be at least 8 characters with uppercase, lowercase, number and special character"), (value) => {
          if (!value) return false; // Will trigger required message
          if (value.length < 8) return false;
          if (value.length > 255) return false;
          if (!PASSWORD_REGEX.test(value)) return false;
          return true;
        })
        .required(t(change_password.validation.new_password?.required || "New password is required")),
      confirm_password: yup
        .string()
        .oneOf(
          [yup.ref("new_password")],
          t(change_password.validation.confirm_password?.same_password || "Confirm password must match new password")
        )
        .required(t(change_password.validation.confirm_password?.required || "Confirm password is required")),
    });
  }

  /**
   * @name editProfileValidation
   * @description Validates the input fields for editing the user profile.
   * According to the API, full_name, email, and contact_number are optional.
   * @param {function} t - Translation function
   * @returns {Object} A Yup validation schema for the edit profile form.
   * @throws {Error} If validation fails (missing or invalid fields).
   */
  static editProfileValidation(t) {
    const { edit_profile } = validation;
    return yup.object().shape({
      full_name: yup
        .string()
        .required(t(edit_profile.first_name.required))
        .max(VALIDATION_CONSTANTS.FULL_NAME_MAX_LENGTH, t(edit_profile.first_name.max_length))
        .matches(FULL_NAME_REGEX, t(edit_profile.first_name.invalid_format)),
      email: yup
        .string() 
        .email(t(edit_profile.email.invalid_format))
        .max(VALIDATION_CONSTANTS.EMAIL_MAX_LENGTH, t(edit_profile.email.max_length)),
      contact_number: yup
        .string()
        .min(VALIDATION_CONSTANTS.CONTACT_NUMBER_MIN_LENGTH, t(edit_profile.contact_number.min_length))
        .max(VALIDATION_CONSTANTS.CONTACT_NUMBER_MAX_LENGTH, t(edit_profile.contact_number.max_length)),
    });
  }

  /**
   * @name emailValidation
   * @description Validates the email input field for the edit profile form.
   * @param {function} t - Translation function
   * @returns {Object} A Yup validation schema for email validation.
   * @throws {Error} If validation fails (invalid format or missing email).
   */
  static newEmailValidation(t) {
    return yup.object().shape({
      email: yup
        .string()
        .email(t(edit_profile.update_email.validation.email.pattern))
        .required(t(edit_profile.update_email.validation.email.required)),
    });
  }

  /**
   * @name newEmailOtpValidation
   * @description Validates the OTP input field for the edit profile form.
   * @param {function} t - Translation function
   * @returns {Object} A Yup validation schema for OTP validation.
   * @throws {Error} If validation fails (not a 6-digit number or missing OTP).
   */
  static newEmailOtpValidation(t) {
    return yup.object().shape({
      otp: yup
        .string()
        .matches(OTP_REGEX, t(edit_profile.otp_verify.validation.otp.pattern))
        .required(t(edit_profile.otp_verify.validation.otp.required)),
    });
  }

  /**
   * Returns a Yup validation schema for verifying an MFA OTP.
   * @param {function} t - Translation function
   * @returns {yup.ObjectSchema} A Yup schema that validates a 6-digit numeric OTP.
   */
  static mfaEnableVerifyOTP(t) {
    return yup.object().shape({
      otp: yup
        .string()
        .matches(OTP_REGEX, t(edit_profile.otp_verify.validation.otp.pattern))
        .required(t(edit_profile.otp_verify.validation.otp.required)),
    });
  }

  /**
   * @name translationValidation
   * @description Validates the translation key and translation values for translation forms.
   * @returns {Object} A Yup validation schema for translation forms.
   * @throws {Error} If validation fails (missing or invalid fields).
   */
  static translationValidation() {
    const { translation } = validation;
    return yup.object().shape({
      key: yup
        .string()
        .required(translation.key.required)
        .min(VALIDATION_CONSTANTS.TRANSLATION_KEY_MIN_LENGTH, translation.key.min_length)
        .max(VALIDATION_CONSTANTS.TRANSLATION_KEY_MAX_LENGTH, translation.key.max_length),
      translations: yup.array().of(
        yup.object().shape({
          title: yup
            .string()
            .required(translation.title.required)
            .min(VALIDATION_CONSTANTS.TRANSLATION_TITLE_MIN_LENGTH, translation.title.min_length)
            .max(VALIDATION_CONSTANTS.TRANSLATION_TITLE_MAX_LENGTH, translation.title.max_length)
        })
      ).min(1, translation.translations.min_items)
    });
  }
}
export default validationHelper;
